import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { RolloutInfo } from '../models/rollout-history'
import { VectorSourceSpecification } from 'maplibre-gl'
import { firstValueFrom } from 'rxjs'
import { formatDateToYYYYMMDD } from '../utils/date.utils'
import { AppStateService } from 'src/app/core/services/app-state.service'

@Injectable()
export class DataRolloutTilesService {
  private maxZoomAvailable = 18
  private minZoomAvailable = 5

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async getRolloutByTimestamp(
    timestamp: Date | undefined,
    datasource: string,
  ): Promise<VectorSourceSpecification | null> {
    let date
    if (timestamp !== undefined && this.appState.isHistoryEnabled()) {
      date = formatDateToYYYYMMDD(timestamp)
      return await this.getRolloutTileByDate(date, datasource)
    } else {
      return await this.getLatestRolloutTile(datasource)
    }
  }

  async getRolloutTileByDate(
    date: string,
    datasource: string,
  ): Promise<VectorSourceSpecification | null> {
    const rollout = await firstValueFrom(
      this.http.get<RolloutInfo>(`${this.config.backend_url}vzo/rolloutTileByDate/${date}`, {
        params: { datasource },
      }),
    )
    if (rollout) {
      const rolloutTimestamp = this.normalizeUtcTimestamp(rollout.description)
      if (rolloutTimestamp) {
        this.updateRolloutDateState(rolloutTimestamp)
      }
      return this.buildTileSource(rollout.id)
    } else {
      return null
    }
  }

  async getLatestRolloutTile(datasource: string): Promise<VectorSourceSpecification | null> {
    const tileId = this.config.latest_rollout_tile_ids?.[datasource]
    if (!tileId) {
      return null
    }
    const staticTimestamp = await this.fetchStaticDate(datasource)
    if (staticTimestamp) {
      this.updateRolloutDateState(staticTimestamp)
    }
    return this.buildTileSource(tileId)
  }

  async fetchStaticDate(datasource: string): Promise<string | null> {
    const tileId = this.config.latest_rollout_tile_ids?.[datasource]
    if (!tileId) return null
    const metadataUrl = `https://api.maptiler.com/tiles/${tileId}/tiles.json?key=${this.config.maptiler_key}`
    try {
      const metadata = await firstValueFrom(this.http.get<{ description?: string }>(metadataUrl))
      return this.normalizeUtcTimestamp(metadata?.description)
    } catch {
      return null
    }
  }

  private normalizeUtcTimestamp(timestamp?: string): string | null {
    if (!timestamp) {
      return null
    }
    const hasTimezone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(timestamp)
    const date = new Date(hasTimezone ? timestamp : `${timestamp}Z`)
    return isNaN(date.getTime()) ? null : date.toISOString()
  }

  private buildTileSource(tileId: string): VectorSourceSpecification {
    return {
      type: 'vector',
      tiles: [
        `https://api.maptiler.com/tiles/${tileId}/{z}/{x}/{y}.pbf?key=${this.config.maptiler_key}`,
      ],
      maxzoom: this.maxZoomAvailable,
      minzoom: this.minZoomAvailable,
    }
  }

  private updateRolloutDateState(rolloutDate: string): void {
    const appPath = this.appState.getCurrentAppPath()
    if (appPath) {
      this.appState.setState(appPath, { rolloutDate })
    }
  }
}
